import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import { useEffect } from 'react';
import { Tab, UserState } from './UserState';
import { clearUser, get, setActiveTab, userState } from './UserSlice';
import { setError } from '../../error/ErrorHandlerSlice';
import ZendeskUserAssociation from '../../zendesk/ZendeskUser/ZendeskUserAssociation';
import UserGeneralTab from './userGeneralTab/UserGeneralTab';
import UserBillingTab from './userBillingTab/UserBillingTab';
import UserCountersTab from './userCountersTab/UserCountersTab';
import UserSettingsTab from './userSettingsTab/UserSettingsTab';
import UserUpdateModal from './userUpdateModal/UserUpdateModal';
import UserActionsTab from './userActionsTab/UserActionsTab';
import LoginUsersTab from '../../loginUsers/loginUsersTab/LoginUsersTab';
import SsoTab from '../../sso/SsoTab';
import IgnoredUrlsTab from './ignoredUrlsTab/IgnoredUrlsTab';

const UserComponent = () => {
  const { userId } = useParams();
  const state: UserState = useAppSelector(userState);
  const dispatch = useAppDispatch();
  const location = useLocation();
  const navigate = useNavigate();

  const { user, originalUser, activeTab } = state;

  useEffect(() => {
    if (userId) dispatch(get(parseInt(userId, 10)));
  }, [dispatch, userId]);

  useEffect(() => {
    return () => {
      dispatch(clearUser());
    };
  }, [dispatch]);

  useEffect(() => {
    if (state.error) dispatch(setError(state.error));
  }, [dispatch, state.error]);

  useEffect(() => {
    const hash: string = location.hash;
    if (!hash) {
      navigate(location.pathname + '#general');
    } else {
      let tab = Tab.general;
      switch (hash) {
        case '#billing':
          tab = Tab.billing;
          break;

        case '#counters':
          tab = Tab.counters;
          break;

        case '#settings':
          tab = Tab.settings;
          break;

        case '#actions':
          tab = Tab.actions;
          break;

        case '#loginUsers':
          tab = Tab.loginUsers;
          break;

        case '#ignoredUrls':
          tab = Tab.ignoredUrls;
          break;

        case '#sso':
          tab = Tab.sso;
          break;

        case '#general':
        default:
          tab = Tab.general;
          break;
      }
      dispatch(setActiveTab(tab));
    }
  }, [dispatch, location.hash, location.pathname, navigate]);

  if (!user) {
    return null;
  }
  return (
    <div>
      {originalUser && <UserUpdateModal user={user} originalUser={originalUser} />}

      <div className="card">
        <div className="card-body">
          <div className="header">
            <div className="header-body pb-0">
              <div className="row align-items-end">
                <div className="col">
                  <h6 className="header-pretitle">User</h6>

                  <h1 className="header-title">{user.adminEmail}</h1>
                </div>
                <div className="col-auto">
                  <img
                    src={user.circleSrc}
                    width="24px"
                    height="24px"
                    alt="user color circle"
                    className="rounded-circle"
                  ></img>
                </div>
              </div>
              <div className="row">
                <div className="col">
                  <ul className="nav nav-tabs header-tabs mt-4">
                    {(Object.keys(Tab) as Array<keyof typeof Tab>).map((tab) => {
                      return (
                        <li className="nav-item" key={tab}>
                          <Link
                            to={{ hash: `#${tab}` }}
                            className={`nav-link text-center px-3 py-1 ${activeTab === Tab[tab] ? 'active' : ''}`}
                          >
                            {tab}
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </div>
            </div>
          </div>
          {activeTab === Tab.general && <UserGeneralTab user={user}></UserGeneralTab>}
          {activeTab === Tab.billing && <UserBillingTab user={user}></UserBillingTab>}
          {activeTab === Tab.counters && <UserCountersTab user={user}></UserCountersTab>}
          {activeTab === Tab.settings && <UserSettingsTab user={user}></UserSettingsTab>}
          {activeTab === Tab.ignoredUrls && <IgnoredUrlsTab user={user}></IgnoredUrlsTab>}
          {activeTab === Tab.actions && <UserActionsTab user={user} />}
          {activeTab === Tab.loginUsers && <LoginUsersTab user={user} />}
          {activeTab === Tab.sso && <SsoTab user={user} />}
        </div>
      </div>

      <div className="row align-items-center">
        <ZendeskUserAssociation user={user} />
      </div>
    </div>
  );
};

export default UserComponent;
